/**
 * Authentication context and hooks
 */

import { createContext, useContext, useState, type ReactNode } from 'react'
import type { AuthState, LoginRequest } from '../types'
import { useAuthInit } from './use-auth/use-auth-init'
import { useAuthActions } from './use-auth/use-auth-actions'

interface AuthContextType extends AuthState {
  login: (credentials: LoginRequest) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  /** Clear the passwordMustChange flag after successful password change */
  clearPasswordMustChange: () => void
}

const AuthContext = createContext<AuthContextType | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps): React.ReactElement {
  const [state, setState] = useState<AuthState>({
    user: null,
    accessToken: null,
    isAuthenticated: false,
    isLoading: true,
  })

  useAuthInit(setState)
  const actions = useAuthActions(setState)

  return <AuthContext.Provider value={{ ...state, ...actions }}>{children}</AuthContext.Provider>
}

/**
 * Hook to access auth context
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

/**
 * Hook to check if user has a specific role
 */
export function useHasRole(
  ...roles: Array<'evidenx_admin' | 'org_admin' | 'investigator' | 'analyst'>
): boolean {
  const { user } = useAuth()
  if (!user) return false
  return roles.includes(user.role)
}

/**
 * Hook to check if user is platform admin
 */
export function useIsPlatformAdmin(): boolean {
  return useHasRole('evidenx_admin')
}

/**
 * Hook to check if user has any admin role
 */
export function useIsAdmin(): boolean {
  return useHasRole('evidenx_admin', 'org_admin')
}
